/**
 * Which catalog tags are regions, and which subtype tags are not a champion.
 *
 * A card's tags are region names plus subtypes (champion name, creature type,
 * Equipment). Keywords are separate: they are ability words parsed from
 * `[brackets]` in rules text, not tags.
 *
 * Framework-free so the gallery sync, the Riftcodex normalizer, facet SQL,
 * and the champion matcher share one definition.
 */

/** Region names that appear inside `card.tags`. Includes split labels and aliases. */
export const REGION_TAGS = [
  'Demacia',
  'Noxus',
  'Freljord',
  'Ionia',
  'Shadow Isles',
  'Piltover & Zaun',
  'Piltover',
  'Zaun',
  'Bilgewater',
  'Targon',
  'Mount Targon',
  'Shurima',
  'Bandle City',
  'Runeterra',
  'Ixtal',
  'The Void',
];

/**
 * Subtype tags that ride along on a champion card and are not the identity.
 * "Yordle" on Hexplosives Expert is a creature subtype; the identity is Ziggs.
 * "Equipment" on a signature gear is the gear subtype, not the champion.
 */
export const NON_CHAMPION_SUBTYPE_TAGS = [
  'Dog',
  'Cat',
  'Poro',
  'Bird',
  'Mech',
  'Recruit',
  'Yordle',
  'Equipment',
  'Spirit',
  'Elite',
  'Demon',
  'Dragon',
  'Trifarian',
];

/**
 * `[...]` tokens that are icons or costs, not keyword abilities.
 * The activated-ability arrow is included so it is not a searchable keyword.
 */
const SYMBOL_BRACKETS = new Set([
  'Might',
  'Ready',
  'Fury',
  'Calm',
  'Mind',
  'Body',
  'Chaos',
  'Order',
  'Rune',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '0',
  '>',
]);

const REGION_TAG_SET = new Set(REGION_TAGS);
const NON_CHAMPION_SUBTYPE_SET = new Set(NON_CHAMPION_SUBTYPE_TAGS);

const HTML_ENTITIES = { '&gt;': '>', '&lt;': '<', '&amp;': '&', '&quot;': '"', '&#39;': "'" };

export function isRegionTag(tag) {
  return REGION_TAG_SET.has(tag);
}

export function isNonChampionSubtypeTag(tag) {
  return NON_CHAMPION_SUBTYPE_SET.has(tag);
}

/**
 * Champion name stored as a tag. Skips regions and creature/gear subtypes.
 * A legend title such as "Pride of Nazumah" is not a tag, so it is never returned.
 */
export function championIdentityTag(tags) {
  return (tags || []).find((tag) => tag && !isRegionTag(tag) && !isNonChampionSubtypeTag(tag));
}

/** SQL literal list for `tag NOT IN (...)`. Values are the constant region list. */
export function regionTagsSqlList() {
  return `(${REGION_TAGS.map((tag) => `'${String(tag).replace(/'/g, "''")}'`).join(',')})`;
}

function decodeHtmlEntities(str) {
  return String(str).replace(/&(?:gt|lt|amp|quot|#39);/g, (match) => HTML_ENTITIES[match] ?? match);
}

/** Keyword abilities from card text. Symbol brackets are omitted. */
export function extractKeywords(text) {
  if (!text) return [];
  const decoded = decodeHtmlEntities(text);
  const matches = decoded.match(/\[([^\]]+)\]/g) || [];
  return [...new Set(
    matches
      .map((match) => match.slice(1, -1))
      .filter((token) => token && !SYMBOL_BRACKETS.has(token)),
  )];
}
